// i18n Type Definitions

/** Single source of truth for supported locales (runtime + type). D7-16. */
export const SUPPORTED_LOCALES = ['cs', 'en', 'de', 'fr', 'it', 'nl', 'pl', 'pt', 'es'] as const
export type SupportedLocale = typeof SUPPORTED_LOCALES[number]

/** Homepage path for a locale: English lives on the root, others under /{locale}. */
export function localePath(locale: SupportedLocale): string {
  return locale === 'en' ? '/' : `/${locale}`
}

export interface TranslationKeys {
  // Common UI elements
  common: {
    loading: string
    error: string
    success: string
    save: string
    cancel: string
    delete: string
    delete_short: string
    edit: string
    create: string
    update: string
    search: string
    filter: string
    clear: string
    yes: string
    no: string
    back: string
    back_to_homepage: string
    next: string
    previous: string
    close: string
    open: string
    copy: string
    copied: string
    confirm: string
    warning: string
    info: string
    continue: string
    open_in_new_window: string
    words: string
    of: string
    active: string
    inactive: string
    more_info: string
  }

  // Authentication
  auth: {
    login: string
    logout: string
    email: string
    password: string
    signin: string
    signout: string
    welcome_back: string
    enter_email: string
    enter_password: string
    invalid_credentials: string
    login_success: string
    logout_success: string
    session_expired: string
    login_to_account: string
    enter_credentials: string
    email_placeholder: string
    password_placeholder: string
    logging_in: string
    login_successful: string
    no_account: string
    register: string
    register_successful: string
    register_confirmation_sent: string
    confirm_password_placeholder: string
    passwords_do_not_match: string
    connection_error: string
    captcha_failed: string
    created_for_czech_offices: string
    session_expired_title: string
    session_expired_description: string
    session_expired_login: string
    session_expired_go_home: string
    two_factor_code: string
    two_factor_code_placeholder: string
    two_factor_verify: string
    two_factor_back: string
    two_factor_info: string
    two_factor_invalid_code: string
    or_continue_with: string
    signin_with_google: string
    google_oauth_error: string
    forgot_password: string
    forgot_password_title: string
    forgot_password_description: string
    forgot_password_email_sent: string
    forgot_password_back: string
    reset_password_title: string
    reset_password_new_password: string
    reset_password_confirm: string
    reset_password_success: string
    reset_password_invalid_link: string
    register_subtitle: string
    email_not_confirmed: string
    user_already_exists: string
    weak_password: string
    same_password: string
    too_many_requests: string
    invalid_email: string
    request_failed: string
  }

  // Dashboard
  dashboard: {
    title: string
    welcome: string
    overview: string
    statistics: string
    total_chatbots: string
    total_messages: string
    total_requests: string
    total_documents: string
    active_conversations: string
    active_chatbots: string
    monthly_usage: string
    monthly_limit: string
    current_cost: string
    projected_cost: string
    current_messages: string
    projected_messages: string
    usage_percentage: string
    quick_actions: string
    recent_activity: string
    view_all: string
    total_chatbots_desc: string
    active_chatbots_desc: string
    total_requests_desc: string
    total_documents_desc: string
    top_requests: string
    top_requests_desc: string
    no_requests_yet: string
    times_asked: string
    requests_unit: string
    current_tier_limits: string
    chatbot_unit_singular: string
    chatbot_unit_plural: string
    extra_messages_available: string
    extra_messages_remaining: string
    extra_messages_active: string
    consuming_extra_messages: string
    extra_messages_standby: string
    extra_messages_unit: string
    using_extra_messages: string
    available: string
  }

  // Chatbots
  chatbots: {
    title: string
    create_new: string
    create_chatbot: string
    edit_chatbot: string
    delete_chatbot: string
    chatbot_name: string
    description: string
    response_style: string
    theme_color: string
    fallback_message: string
    max_response_length: string
    cache_ttl: string
    is_active: string
    widget_id: string
    created_at: string
    last_updated: string
    message_count: string
    document_count: string
    no_chatbots: string
    create_first_chatbot: string
    chatbot_created: string
    chatbot_updated: string
    chatbot_deleted: string
    copy_widget_code: string
    open_in_new_window: string
    preview_chatbot: string
    limit_reached_title: string
    limit_reached_message: string
    basic_information: string
    basic_information_desc: string
    appearance: string
    appearance_desc: string
    welcome_message: string
    welcome_message_placeholder: string
    fallback_message_placeholder: string
    fallback_message_hint: string
    handoff_contact: string
    handoff_contact_placeholder: string
    handoff_contact_hint: string
    allowed_domains: string
    allowed_domains_placeholder: string
    allowed_domains_hint: string
    create_page_subtitle: string
    my_chatbots: string
    my_chatbots_subtitle: string
    new_chatbot: string
    loading_chatbots: string
    no_chatbots_title: string
    no_chatbots_description: string
    create_first_chatbot_button: string
    no_description: string
    status: string
    active: string
    inactive: string
    queries: string
    documents: string
    settings: string
    copy_code: string
    preview_widget: string
    edit: string
    delete: string
    delete_confirmation: string
    delete_error: string
    unexpected_error: string
    widget_code_copied: string
    embed_instructions: string
    embed_tip_title: string
    embed_tip_body: string
    embed_gtm_warning: string
    edit_chatbot_title: string
    edit_chatbot_subtitle: string
    widget_embedding: string
    widget_embedding_desc: string
    widget_id_hint: string
    allowed_domains_hint_edit: string
    chatbot_name_placeholder: string
    description_placeholder: string
    chatbot_is_active: string
    theme_color_label: string
    widget_position: string
    widget_position_bottom_right: string
    widget_position_bottom_left: string
    enable_bubble_pulse: string
    enable_bubble_pulse_hint: string
    enable_tickets: string
    enable_tickets_hint: string
    welcome_message_placeholder_edit: string
    placeholder_text: string
    placeholder_text_placeholder: string
    placeholder_text_hint: string
    ai_behavior_prompts: string
    ai_behavior_desc: string
    basic_instructions: string
    basic_instructions_placeholder: string
    basic_instructions_hint: string
    language_preference: string
    select_language: string
    timezone: string
    select_timezone: string
    timezone_hint: string
    creativity_temperature: string
    creativity_hint: string
    max_response_length_tokens: string
    search_quality: string
    search_quality_desc: string
    similarity_threshold: string
    similarity_threshold_hint: string
    context_documents_count: string
    context_documents_hint: string
    conversation_memory: string
    conversation_memory_hint: string
    force_fallback: string
    force_fallback_hint: string
    enable_query_rewriting: string
    enable_query_rewriting_hint: string
    enable_reranking: string
    enable_reranking_hint: string
    reranking_upgrade: string
    rate_limit_per_minute: string
    rate_limit_hint: string
    cache_ttl_hint: string
    integrations_section_label: string
    reset_defaults: string
    saving: string
    save_changes: string
    save_changes_short: string
    saving_short: string
    reset_defaults_short: string
    chatbot_not_found: string
    delete_chatbot_confirm: string
    delete_chatbot_warning: string
    response_styles: {
      formal: string
      friendly: string
      professional: string
      casual: string
      formal_desc: string
      friendly_desc: string
      professional_desc: string
      casual_desc: string
    }
    reset_defaults_confirm: string
    default_instruction_prompt: string
    language_options: {
      czech: string
      english: string
      german: string
      french: string
      spanish: string
      italian: string
      portuguese: string
      dutch: string
      polish: string
      slovak: string
      hungarian: string
      russian: string
      auto: string
    }
    language_auto_hint: string
    theme_colors: {
      blue: string
      red: string
      green: string
      yellow: string
      purple: string
      orange: string
      custom_label: string
      custom_placeholder: string
      custom_error: string
      custom_hint: string
    }
    antispam_protection: string
    antispam_protection_desc: string
    max_requests_per_day: string
    max_requests_per_session: string
    max_requests_per_day_hint: string
    max_requests_per_session_hint: string
    language_ai_parameters: string
    language_ai_parameters_desc: string
    tab_general: string
    tab_appearance: string
    tab_ai: string
    tab_security: string
    tab_integrations: string
    tab_documents: string
    enable_tickets_plan_hint: string
    load_error: string
  }

  // Documents
  documents: {
    title: string
    upload_document: string
    drag_drop: string
    or_click_to_browse: string
    supported_formats: string
    filename: string
    status: string
    uploaded_at: string
    processing: string
    completed: string
    failed: string
    upload_success: string
    upload_failed: string
    delete_document: string
    deleting: string
    download_document: string
    document_deleted: string
    no_documents: string
    search_documents: string
    view_mode: string
    grid_view: string
    table_view: string
    loading_documents: string
    no_description: string
    size: string
    date: string
    actions: string
    document: string
    chunks: string
    processed: string
    processing_progress: string
    uploaded: string
    no_filtered_results: string
    clear_filters: string
    results_count: string
    subtitle: string
    delete_document_confirm: string
    delete_error: string
    finalizing: string
    generating_embeddings: string
    processing_content: string
    processing_error: string
    supported_formats_detailed: string
    drop_files_here: string
    url_placeholder: string
    add: string
    enter_url: string
    content_extracted_automatically: string
    upload_error: string
    replace_in_progress: string
    selected_items: string
    files_count: string
    no_files_yet: string
    add_files_hint: string
    recommendation: string
    progress: string
    content_type: string
    content_description_placeholder: string
    content_length: string
    characters: string
    done: string
    upload_all: string
    upload_files: string
    upload_url: string
    created: string
    uploading_to_server: string
    invalid_file_type: string
    invalid_url_format: string
    upload_cancelled_duplicate: string
    upload_cancelled_duplicate_url: string
    select_at_least_one: string
    web_url_desc: string
    existing_document: string
    files: string
    web_url: string
    document_type: string
    description_placeholder: string
    ready_to_upload: string
    uploading: string
    description: string
    selected_files: string
    add_url: string
    url_addresses: string
    upload_items: string
    new_file: string
    description_optional: string
    no_documents_subtitle: string
    subtitle_per_chatbot: string
    status_labels: {
      pending: string
      processing: string
      completed: string
      failed: string
    }
    duplicate_dialog: {
      title: string
      message: string
      replace: string
      keep_both: string
      file_exists: string
      url_exists: string
      replace_question: string
      url_replace_question: string
    }
    web_url_locked_title: string
    web_url_locked_desc: string
    web_url_locked_dialog_title: string
    web_url_locked_dialog_message: string
    file_too_large: string
    processing_eta: string
    url_upload_failed: string
    status_check_failed: string
  }

  // Add-ons
  addons: {
    buy_messages: string
    message_packages: string
    package_description: string
    messages_1000: string
    messages_5000: string
    messages_10000: string
    buy_now: string
    best_value: string
    popular: string
    no_expiration: string
    instant_delivery: string
    checkout_failed: string
    price_per_reply: string
  }

  // Analytics
  analytics: {
    title: string
    query_analytics: string
    query_overview_description: string
    total_queries: string
    today: string
    active_chatbots: string
    most_frequent_query: string
    repetitions: string
    most_frequent_queries: string
    queries_by_frequency: string
    no_queries_recorded: string
    last_asked: string
    times_asked: string
    chatbot_statistics: string
    queries_per_chatbot: string
    success_rate: string
    queries_count: string
    no_description: string
    from_launch: string
    queries_today: string
    chatbots_with_data: string
    failed_to_load: string
    upgrade_for_detailed: string
    detailed_analytics_locked_title: string
    detailed_analytics_locked_message: string
    detailed_upgrade_hint: string
    back_to_overview: string
    period: string
    this_week: string
    this_month: string
    last_90_days: string
    select_period: string
    analytics_for_chatbot: string
    detailed_stats_for_period: string
    export: string
    page_x_of_y: string
    answered: string
    filter_by_quality: string
    all_queries: string
    not_answered: string
    success_rate_short: string
    average_success_rate: string
    confidence: string
    average_confidence: string
    document_similarity_score: string
    answer: string
    for_period: string
    successful_answers: string
    not_answered_excluded: string
    answered_ratio: string
    no_queries_in_period: string
    queries_over_time: string
    no_data_for_period: string
    answered_queries_description: string
    ticket_created_queries_description: string
    tool_call_queries_description: string
    not_classified_queries_description: string
    all_queries_description: string
    ai_answers: string
    characters: string
    no_answer_add_content: string
    conversation: string
    conversation_queries_description: string
    off_topic_queries_description: string
    chatbot_not_found: string
    more_answers: string
    searched_as: string
    view_conversation: string
    avg_response_time: string
    source: string
    filter_by_source: string
    all_sources: string
    source_widget: string
    source_api: string
    source_breakdown: string
    widget_vs_api: string
    answer_quality: {
      answered: string
      not_answered: string
      conversation: string
      off_topic: string
      ticket_created: string
      tool_call: string
      not_classified: string
    }
    no_data: string
    csv_export: {
      query: string
      repeat_count: string
      first_query: string
      last_query: string
      success_rate: string
      avg_confidence: string
      min_confidence: string
      max_confidence: string
      answered: string
      not_answered: string
      conversation: string
      off_topic: string
      ticket_created: string
      tool_call: string
      not_classified: string
    }
    usage_per_chatbot: string
    usage_per_chatbot_description: string
    no_chatbot_data: string
  }

  // Widget
  widget: {
    welcome_message: string
    placeholder: string
    error_message: string
    offline_message: string
    thinking: string
    type_message: string
    send: string
    minimize: string
    expand: string
    new_conversation: string
    powered_by: string
    open_chat: string
    load_error: string
    domain_not_allowed: string
    online: string
    limit_daily: string
    limit_session: string
    limit_rate: string
  }

  // Pricing
  pricing: {
    current_plan: string
  }

  // Navigation
  navigation: {
    overview: string
    chatbots: string
    analytics: string
    settings: string
    help: string
    blog: string
    profile: string
    subscription: string
    account_management: string
    my_account: string
    user: string
    loading_auth: string
    not_logged_in: string
    go_to_login: string
    sign_out: string
    dashboard: string
    need_help: string
    contact_support: string
    support_email: string
    api_integration: string
    conversations: string
    tickets: string
    theme: string
    theme_light: string
    theme_dark: string
    language: string
  }

  languages: {
    en: string
    cs: string
    de: string
    fr: string
    it: string
    nl: string
    pl: string
    pt: string
    es: string
  }

  // Errors
  errors: {
    generic: string
    network: string
    unauthorized: string
    forbidden: string
    not_found: string
    server_error: string
    validation_error: string
    file_too_large: string
    unsupported_format: string
    upload_failed: string
    processing_failed: string
    rate_limit_exceeded: string
    quota_exceeded: string
    insufficient_permissions: string
  }

  // Success messages
  success: {
    saved: string
    updated: string
    deleted: string
    created: string
    uploaded: string
    copied: string
    exported: string
    processed: string
  }

  // Subscription management
  subscription: {
    errors: {
      upgrade_failed: string
      period_change_failed: string
      period_paid_only: string
      downgrade_failed: string
      portal_failed: string
    }
    title: string
    description: string
    choose_plan: string
    current_plan: string
    active: string
    usage_consumed: string
    next_reset: string
    evaluation_expires: string
    evaluation_expiring_warning: string
    expires_tomorrow: string
    expires_in_days: string
    manage_subscription: string
    change_period: string
    downgrade: string
    auto_renewal: string
    subscription_expires: string
    free: string
    monthly_quota: string
    extra_messages: string
    using_extra_messages: string
    extra_messages_badge: string
    billing_period: {
      monthly: string
      yearly: string
      months_free: string
    }
    available_plans: string
    cancellation_pending: string
    plan_descriptions: {
      free: string
      evaluation: string
      support: string
      automate: string
    }
    plan_change: {
      title: string
      description: string
      current: string
      new: string
      active: string
      immediate: string
      from_date: string
      savings: string
      extra_cost: string
      savings_description: string
      extra_cost_description: string
      important_notes: string
      note_charge_now: string
      note_scheduled: string
      note_limits: string
      note_portal: string
      processing: string
      confirm: string
      success: string
      success_scheduled: string
      scheduled_label: string
      scheduled_value: string
      cancel_scheduled: string
      cancel_scheduled_success: string
      error_cancel_scheduled: string
    }
    polar: {
      managed_via: string
    }
    plan_cards: {
      popular: string
      trial: string
      upgrade: string
      everything_in_support: string
      chatbots_one: string
      chatbots_many: string
      limits_month: string
      limits_trial: string
      storage: string
      smart_context_search: string
      basic_analytics: string
      api_mcp: string
      eval_everything: string
      eval_then_free: string
      documents: string
      url_content: string
      tickets_lead: string
      detailed_analytics: string
      smart_search_reranking: string
      actions: string
      white_label: string
      billed_yearly: string
      scheduled: string
      need_more: string
      contact_us: string
      trial_strip_title: string
      trial_strip_text: string
      trial_strip_white_label: string
      trial_badge: string
      start_free: string
      try_free: string
      micro_free: string
      micro_support: string
      micro_automate: string
    }
    comparison: {
      show: string
      hide: string
      feature: string
      chatbots: string
      replies: string
      conversations: string
      storage: string
      per_month: string
      per_trial: string
      widget: string
      documents: string
      history: string
      smart_context_search: string
      api_mcp: string
      basic_analytics: string
      url_content: string
      tickets: string
      detailed_analytics: string
      analytics_export: string
      reranking: string
      reranking_note: string
      webhooks: string
      white_label: string
      extra_replies: string
    }
    plan_features: {
      per_month: string
      current_badge: string
      current_plan_label: string
      current_trial_label: string
    }
    profile_not_found: string
    profile_not_found_hint: string
    checkout_processing: string
  }

  // Usage limits and restrictions
  limits: {
    storage_limit_exceeded: string
    storage_limit_message: string
    chatbot_limit_exceeded: string
    chatbot_limit_message: string
    upgrade_to_continue: string
    current_usage: string
    available_space: string
    storage_usage: string
    chatbot_usage: string
    of_limit: string
    unlimited: string
  }

  // Upgrade dialogs and prompts
  upgrade: {
    current_plan: string
    required_plan: string
    recommended: string
    upgrade_to: string
    limited_access: string
    upgrade_plan: string
  }

  // Homepage content
  homepage: {
    brand_name: string
    brand_subtitle: string
    loading: string
    nav: {
      features: string
      pricing: string
      sign_in: string
      faq: string
      help: string
      blog: string
    }
    hero: {
      title_line1: string
      title_line2: string
      subtitle: string
      cta_button: string
      trial_info: string
      trial_footer: string
      mascot_alt: string
      trial_flow: {
        step1: string
        step1_desc: string
        step1_highlight: string
        step2: string
        step2_desc: string
        step2_desc_end: string
        step2_highlight: string
        step3: string
        step3_desc: string
        step3_highlight: string
      }
    }
    onboarding: {
      badge: string
      title_line1: string
      title_line2: string
      subtitle: string
      avg_setup: string
      avg_setup_highlight: string
      step1_title: string
      step1_desc: string
      step2_title: string
      step2_desc: string
      step3_title: string
      step3_desc: string
      mascot_alt: string
    }
    support: {
      badge: string
      title_line1: string
      title_line2: string
      subtitle: string
      stat1_num: string
      stat1_label: string
      stat2_num: string
      stat2_label: string
      stat3_num: string
      stat3_label: string
      chat_bot_name: string
      chat_online: string
      chat_msg1: string
      chat_question: string
      chat_msg2: string
      chat_input_placeholder: string
      mascot_alt: string
    }
    loop: {
      badge: string
      title_line1: string
      title_line2: string
      subtitle: string
      step1_title: string
      step1_desc: string
      step2_title: string
      step2_desc: string
      step3_title: string
      step3_desc: string
      step4_title: string
      step4_desc: string
      video_label: string
      callout1_title: string
      callout1_desc: string
      callout2_title: string
      callout2_desc: string
    }
    automation: {
      badge: string
      title_line1: string
      title_line2: string
      subtitle: string
      diagram_alt: string
      diagram_chatbot: string
      diagram_agent: string
      card1_title: string
      card1_desc: string
      card2_title: string
      card2_desc: string
      card3_title: string
      card3_desc: string
      card3_badge: string
      flow_label: string
      flow_customer: string
      flow_action: string
      flow_response: string
    }
    pricing_value: {
      badge: string
      title_line1: string
      title_line2: string
      subtitle: string
      competitors_label: string
      competitors_price: string
      competitors_per_month: string
      competitors_note: string
      competitors_con1: string
      competitors_con2: string
      competitors_con3: string
      competitors_con4: string
      competitors_con5: string
      chatbotty_label: string
      chatbotty_price: string
      chatbotty_price_suffix: string
      chatbotty_note: string
      chatbotty_pro1: string
      chatbotty_pro2: string
      chatbotty_pro3: string
      chatbotty_pro4: string
      chatbotty_pro5: string
    }
    enterprise: {
      badge: string
      title_line1: string
      title_line2: string
      subtitle: string
      card1_title: string
      card1_desc: string
      card2_title: string
      card2_desc: string
      mascot_alt: string
    }
    trust: {
      label: string
      tagline: string
      stat1: string
      stat2: string
      stat3: string
      feature1: string
      feature2: string
      feature3: string
      feature4: string
      feature5: string
      feature6: string
      feature7: string
      feature8: string
      feature9: string
      feature10: string
      feature11: string
      feature12: string
      mascot_alt: string
    }
    pricing_section: {
      title: string
      subtitle: string
    }
    trial_section: {
      annual_savings: string
      flexibility_note: string
      vat_note: string
      cta_button: string
    }
    final_cta: {
      title_line1: string
      title_line2: string
      subtitle: string
      cta_button: string
      footer_note: string
      mascot_alt: string
    }
    support_tickets_section: {
      badge: string
      title_line1: string
      title_line2: string
      subtitle: string
      conversation_note: string
      bullet1: string
      bullet2: string
      bullet3: string
      bullet4: string
      mock_label: string
      mock_number: string
      mock_status: string
      mock_email: string
      mock_email_label: string
      mock_summary: string
      mock_summary_label: string
      status_open: string
      status_in_progress: string
      status_resolved: string
      mascot_alt: string
    }
    footer: {
      links: {
        terms: string
        privacy: string
        contact: string
      }
    }
  }

  // Account management
  account: {
    title: string
    subtitle: string
    personal_info: string
    name: string
    name_placeholder: string
    email: string
    email_placeholder: string
    password: string
    change_name: string
    change_email: string
    change_password: string
    current_password: string
    new_password: string
    confirm_password: string
    name_updated: string
    email_updated: string
    password_updated: string
    password_otp_sent: string
    password_otp_code: string
    password_otp_placeholder: string
    password_otp_confirm: string
    password_otp_invalid: string
    name_update_error: string
    email_update_error: string
    password_update_error: string
    password_mismatch: string
    password_too_short: string
    current_password_required: string
    email_confirmation_note: string
    delete_account: string
    delete_account_warning: string
    danger_zone_description: string
    delete_account_description: string
    delete_account_confirmation: string
    confirm_delete: string
    account_delete_error: string
    account_delete_subscription_error: string
    delete_password_label: string
    delete_password_incorrect: string
    delete_reauth_hint: string
    delete_reauth_required: string
    delete_reauth_button: string
    delete_too_many_attempts: string
    language_settings: string
    language_preference: string
    language_preference_desc: string
    language_czech: string
    language_english: string
    language_updated: string
    language_update_error: string
    defaults_info: string
    two_factor_title: string
    two_factor_description: string
    two_factor_enable: string
    two_factor_disable: string
    two_factor_scan_qr: string
    two_factor_manual_entry: string
    two_factor_verify_code: string
    two_factor_active: string
    two_factor_inactive: string
    two_factor_confirm_disable: string
    connected_accounts: string
    connected_accounts_desc: string
    google_connected: string
    google_not_connected: string
    google_connect_desc: string
    set_password_desc: string
    set_password_btn: string
    set_password_success: string
    name_required: string
    current_password_incorrect: string
    two_factor_error: string
  }

  // API Keys
  api_keys: {
    title: string
    create_new: string
    name_placeholder: string
    delete_title: string
    delete_description: string
    deleted: string
    created: string
    copy_key: string
    key_copied: string
    show_key: string
    hide_key: string
    last_used: string
    never: string
    active: string
    inactive: string
    activate: string
    deactivate: string
    delete: string
    max_limit_reached: string
    key_exposed_warning: string
    no_keys: string
    no_keys_desc: string
    error_loading_keys: string
    error_creating_key: string
    error_deleting_key: string
    error_updating_key: string
    docs_link_title: string
    docs_link_desc: string
    docs_link_button: string
  }

  // Cookie consent banner
  cookie_consent: {
    title: string
    description: string
    accept_all: string
    decline: string
  }

  // SEO metadata
  seo: {
    title: string
    description: string
    keywords: string[]
    openGraph: {
      title: string
      description: string
      siteName: string
      locale: string
    }
    twitter: {
      title: string
      description: string
      card: string
    }
    structuredData: {
      name: string
      description: string
      applicationCategory: string
      operatingSystem: string
      offers: {
        price: string
        priceCurrency: string
        availability: string
      }
    }
  }

  // API Integration
  api_integration: {
    title: string
    subtitle: string
    no_chatbots: string
    create_chatbot: string
    rate_limit_minute: string
    rate_limit_daily: string
    rate_limit_session: string
    docs_link_title: string
    docs_link_desc: string
    docs_link_button: string
    replace_key_hint: string
    replace_key_ticket_hint: string
    replace_key_webhook_hint: string
    path_mcp_title: string
    path_mcp_desc: string
    path_rest_title: string
    path_rest_desc: string
    path_cta: string
    keys_title: string
    keys_desc: string
    keys_security: string
    rest_title: string
    rest_desc: string
    rest_base_url: string
    rest_auth: string
    rest_limits: string
  }

  // Conversations
  conversations: {
    title: string
    subtitle: string
    no_conversations: string
    loading: string
    failed_to_load: string
    source_widget: string
    source_api: string
    status_active: string
    status_completed: string
    status_ended: string
    status_ticket_created: string
    all_chatbots: string
    all_sources: string
    date: string
    source: string
    message_count: string
    status: string
    email: string
    page_url: string
    view_detail: string
    detail_title: string
    metadata: string
    ip_address: string
    user_agent: string
    referrer_url: string
    started_at: string
    last_activity: string
    messages: string
    ticket_linked: string
    view_ticket: string
    not_found: string
    back_to_list: string
    user_message: string
    assistant_message: string
    unknown: string
    no_messages: string
    chatbot: string
    total_count: string
    messages_short: string
    phone: string
    open_detail: string
    delete: string
    delete_title: string
    delete_description: string
    delete_with_ticket: string
    deleted: string
    messages_limit: string
  }

  // Support Tickets
  tickets: {
    title: string
    subtitle: string
    no_tickets: string
    loading: string
    failed_to_load: string
    ticket_number: string
    email: string
    phone: string
    summary: string
    status: string
    date: string
    all_statuses: string
    all_chatbots: string
    status_open: string
    status_in_progress: string
    status_resolved: string
    status_closed: string
    detail_title: string
    contact_info: string
    back_to_list: string
    not_found: string
    linked_conversation: string
    view_conversation: string
    update_status: string
    status_updated: string
    no_summary: string
    notes: string
    no_notes: string
    notes_placeholder: string
    add_note: string
    note_added: string
    note_deleted: string
    chatbot: string
    total_count: string
    open_detail: string
    delete: string
    delete_title: string
    delete_description: string
    deleted: string
    delete_note: string
  }

  // White-label branding
  white_label: {
    title: string
    show_branding_label: string
    custom_brand_text_label: string
    custom_brand_text_placeholder: string
    plan_hint: string
  }

  // Webhook Actions
  webhooks: {
    title: string
    subtitle: string
    add_webhook: string
    edit_webhook: string
    delete_webhook: string
    webhook_name: string
    webhook_url: string
    http_method: string
    timeout: string
    headers: string
    headers_hint: string
    add_header: string
    tools: string
    add_tool: string
    edit_tool: string
    delete_tool: string
    tool_name: string
    tool_display_name: string
    tool_description: string
    tool_parameters: string
    add_parameter: string
    parameter_name: string
    parameter_type: string
    parameter_description: string
    parameter_required: string
    select_tier_title: string
    no_parameters_hint: string
    tool_no_parameters: string
    test_webhook: string
    test_run: string
    test_result: string
    test_success: string
    test_failed: string
    no_webhooks: string
    no_tools: string
    confirm_delete_webhook: string
    confirm_delete_tool: string
    upgrade_required: string
    upgrade_plan: string
    description_help: string
    description_budget_hint: string
    tool_name_help: string
    saving: string
    testing: string
    loading: string
    failed_to_load: string
    help_text: string
    direct_mode: string
    direct_mode_hint: string
    direct_badge: string
    secret_query: string
    secret_query_hint: string
    add_secret_query: string
    secret_unchanged: string
    secret_removed: string
    secret_restore: string
    secret_value_required: string
    secret_key_required: string
    secret_key_duplicate: string
    endpoint_changed_hint: string
    endpoint_changed_error: string
    keys_moved_title: string
    keys_moved_desc: string
    mcp_tip_title: string
    mcp_tip: string
    mcp_tip_link: string
    tools_count: string
    params_count: string
    usage: string
    usage_hint: string
  }

  // FAQ page
  faq: {
    title: string
    subtitle: string
    back_to_home: string
    category_about: string
    category_pricing: string
    category_features: string
    category_technical: string
    q1: string; a1: string
    q2: string; a2: string
    q3: string; a3: string
    q4: string; a4: string
    q5: string; a5: string
    q6: string; a6: string
    q7: string; a7: string
    q8: string; a8: string
    q9: string; a9: string
    q10: string; a10: string
    q11: string; a11: string
    q12: string; a12: string
    q13: string; a13: string
    q14: string; a14: string
    q15: string; a15: string
    q16: string; a16: string
    q17: string; a17: string
    q18: string; a18: string
    still_have_questions: string
    visit_help_center: string
  }

  // Blog
  blog: {
    title: string
    subtitle: string
    read_more: string
    min_read: string
    published_on: string
    categories: string
    tags: string
    all_articles: string
    no_articles: string
    no_articles_in_category: string
    no_articles_with_tag: string
    page_of: string
    back_to_blog: string
    share: string
    breadcrumb_home: string
    breadcrumb_blog: string
    articles_count: string
    articles_count_one: string
    view_grid: string
    view_list: string
    sort_newest: string
    sort_oldest: string
    clear_tags: string
    search: string
    search_placeholder: string
    search_clear: string
    search_no_results: string
    prev_article: string
    next_article: string
  }
  oauth_consent: {
    title: string
    sign_in_prompt: string
    sign_in: string
    permissions: string
    choose_chatbot: string
    choose_chatbot_placeholder: string
    no_chatbots: string
    create_chatbot: string
    plan_required: string
    upgrade: string
    redirect_notice: string
    loopback_warning: string
    revoke_hint: string
    allow: string
    deny: string
    signed_in_as: string
    switch_account: string
    unknown_client: string
    invalid_request: string
    error_generic: string
  }
  mcp_connector: {
    title: string
    description: string
    server_url: string
    copy: string
    copied: string
    plan_required: string
    upgrade: string
    claude_title: string
    claude_steps: string
    claude_code_title: string
    claude_code_oauth: string
    claude_code_key: string
    n8n_title: string
    n8n_steps: string
    one_chatbot_note: string
    oauth_badge: string
    oauth_key_hint: string
  }
}

export type TranslationKey = keyof TranslationKeys
export type NestedTranslationKey = {
  [K in keyof TranslationKeys]: TranslationKeys[K] extends object
    ? `${K}.${keyof TranslationKeys[K] & string}`
    : K
}[keyof TranslationKeys]

// Helper type for deeply nested keys
export type DeepTranslationKey<T> = T extends object
  ? {
      [K in keyof T]: T[K] extends object
        ? `${K & string}.${DeepTranslationKey<T[K]>}`
        : K & string
    }[keyof T]
  : never

export type AllTranslationKeys = DeepTranslationKey<TranslationKeys>
